import type { Logger } from '@opentelemetry/api-logs';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { userAttributes, getTraceContext } from './instrumentation';
import { allowLog } from './logThrottle';

type AxiosConfigWithTiming = AxiosRequestConfig & { __startTime?: number };

function severityFor(status?: number): 'ERROR' | 'WARN' | 'INFO' {
  if (!status || status >= 500) return 'ERROR';
  if (status >= 400) return 'WARN';
  return 'INFO';
}

function pathOf(config: AxiosConfigWithTiming): string {
  const raw = config.url || '';
  if (!raw) return '';
  let full = raw;
  if (config.baseURL && !raw.startsWith('http')) {
    const base = config.baseURL.endsWith('/') ? config.baseURL.slice(0, -1) : config.baseURL;
    full = `${base}${raw.startsWith('/') ? raw : `/${raw}`}`;
  }
  try {
    return new URL(full).pathname;
  } catch {
    return raw;
  }
}

function emit(
  logger: Logger,
  config: AxiosConfigWithTiming,
  response?: AxiosResponse,
  error?: AxiosError,
): void {
  try {
    const status = response?.status ?? error?.response?.status;
    // Only log failures: backend tracing already captures successful requests in full.
    const isFailure = !!error || (status !== undefined && status >= 400);
    if (!isFailure) return;

    const severityText = severityFor(status);
    const method = (config.method || 'UNKNOWN').toUpperCase();
    const path = pathOf(config);
    // Flood control: don't let a hot failing endpoint spam Loki.
    if (!allowLog(`http|${severityText}|${method}|${path}|${status ?? 0}`)) return;
    const latency = config.__startTime ? Date.now() - config.__startTime : 0;
    const { traceID, spanID } = getTraceContext();
    const clientCode = userAttributes.clientCode || '';
    const applicationId = process.env.REACT_APP_TV_APPLICATION_ID || '';
    const env = process.env.REACT_APP_ENV || '';

    const body = {
      level: severityText.toLowerCase(),
      time: new Date().toISOString(),
      caller: 'httpLogger',
      msg: `${method} ${path}`,
      traceID,
      spanID,
      clientCode,
      applicationId,
      env,
      type: 'RequestResponse',
      method,
      path,
      status: status ?? 0,
      latency,
      error: error?.message || '',
      errorCode: error?.code || '',
    };

    // traceID/spanID are intentionally omitted from attributes — the OTel SDK
    // auto-attaches them as the standard `trace_id`/`span_id` fields when an
    // active span context exists (which is the case for HTTP errors caught
    // inside fetch/xhr instrumentation). Including them here would duplicate
    // the fields and produce two Tempo links in Grafana.
    logger.emit({
      severityText,
      body: JSON.stringify(body),
      attributes: {
        source: 'http-logger',
        level: severityText.toLowerCase(),
        type: 'RequestResponse',
        clientCode,
        applicationId,
        env,
        method,
        path,
        status: String(status ?? 0),
        latency: String(latency),
        ...userAttributes,
      },
    });
  } catch {
    // Logging must never break application flow
  }
}

export function setupHttpLogger(logger: Logger, ...axiosInstances: AxiosInstance[]): () => void {
  const ejectors: Array<() => void> = [];
  for (const axiosInstance of axiosInstances) {
    const reqId = axiosInstance.interceptors.request.use((config) => {
      (config as AxiosConfigWithTiming).__startTime = Date.now();
      return config;
    });
    const resId = axiosInstance.interceptors.response.use(
      (response) => {
        emit(logger, response.config, response);
        return response;
      },
      (error: AxiosError) => {
        emit(logger, (error.config || {}) as AxiosConfigWithTiming, undefined, error);
        return Promise.reject(error);
      },
    );
    ejectors.push(() => {
      axiosInstance.interceptors.request.eject(reqId);
      axiosInstance.interceptors.response.eject(resId);
    });
  }
  return () => ejectors.forEach((e) => e());
}
